import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Map as MapIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { volassisAPI } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Loader } from '@googlemaps/js-api-loader';
import EventsMap from '../components/EventsMap';
import SearchBar from '../components/SearchBar';

// Mock events data - replace with actual API calls
const mockEvents = [
  {
    id: 1,
    title: 'Community Cleanup',
    description: 'Help clean up the local park and surrounding areas',
    date: '2024-02-15',
    time: '09:00',
    duration: 3,
    location: 'Central Park',
    address: '123 Main St, City, State 12345',
    coordinates: { lat: 40.7589, lng: -73.9851 },
    organization: 'Green Earth Initiative',
    maxVolunteers: 20,
    currentVolunteers: 12,
    category: 'Environment',
    status: 'active'
  },
  {
    id: 2,
    title: 'Food Bank Distribution',
    description: 'Distribute food packages to families in need',
    date: '2024-02-20',
    time: '14:00',
    duration: 4,
    location: 'Community Center',
    address: '456 Oak Ave, City, State 12345',
    coordinates: { lat: 40.7505, lng: -73.9934 },
    organization: 'Helping Hands',
    maxVolunteers: 15,
    currentVolunteers: 8,
    category: 'Food Security',
    status: 'active'
  },
  {
    id: 3,
    title: 'Senior Center Visit',
    description: 'Spend time with seniors, play games, and provide companionship',
    date: '2024-02-25',
    time: '10:00',
    duration: 2,
    location: 'Golden Years Center',
    address: '789 Pine St, City, State 12345',
    coordinates: { lat: 40.7569, lng: -73.9865 },
    organization: 'Senior Care Network',
    maxVolunteers: 10,
    currentVolunteers: 5,
    category: 'Senior Care',
    status: 'active'
  }
];

const mapContainerStyle = {
  width: '100%',
  height: '500px'
};

const center = {
  lat: 40.7589,
  lng: -73.9851
};

function Events() {
  const { currentUser, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState(mockEvents);
  const [filteredEvents, setFilteredEvents] = useState(mockEvents);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [mapCenter, setMapCenter] = useState(center);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const mapRef = useRef(null);

  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration: '',
    location: '',
    address: '',
    coordinates: { lat: 40.7589, lng: -73.9851 },
    organization: '',
    maxVolunteers: '',
    category: '',
    status: 'active'
  });

  const categories = [
    'Environment',
    'Food Security',
    'Senior Care',
    'Education',
    'Healthcare',
    'Animal Welfare',
    'Community Development',
    'Disaster Relief'
  ];

  const [mapError, setMapError] = useState(false);

  // Helper to safely clear map children
  const clearMapChildren = () => {
    if (mapRef.current) {
      // Only remove children that are actually present
      const children = Array.from(mapRef.current.childNodes);
      children.forEach(child => {
        if (mapRef.current.contains(child)) {
          try {
            mapRef.current.removeChild(child);
          } catch (e) {
            // Ignore errors if node is already removed
          }
        }
      });
      mapRef.current.innerHTML = '';
    }
  };

  // Initialize Google Maps and clean up on unmount
  useEffect(() => {
    let isMounted = true;
    if (viewMode === 'map' && !map && !mapError) {
      initializeMap();
    }
    return () => {
      markers.forEach(({ marker, infoWindow }) => {
        if (marker) {
          try {
            marker.setMap(null);
          } catch (e) {}
        }
        if (infoWindow && infoWindow.close) {
          try {
            infoWindow.close();
          } catch (e) {}
        }
      });
      setMarkers([]);
      clearMapChildren();
    };
    // eslint-disable-next-line
  }, [viewMode, mapError]);

  const initializeMap = async () => {
    try {
      const loader = new Loader({
        apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE',
        version: 'weekly',
        libraries: ['places']
      });

      const google = await loader.load();
      
      if (mapRef.current) {
        const newMap = new google.maps.Map(mapRef.current, {
          center: center,
          zoom: 12,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
        });

        setMap(newMap);
        addMarkersToMap(newMap);
      }
    } catch (error) {
      console.error('Error loading Google Maps:', error);
      setMapError(true);
      toast.error('Failed to load Google Maps. Map features will be disabled.');
    }
  };

  // Clean up markers when events or map changes
  useEffect(() => {
    // Remove all markers from the map
    markers.forEach(({ marker, infoWindow }) => {
      if (marker) {
        try {
          marker.setMap(null);
        } catch (e) {}
      }
      if (infoWindow && infoWindow.close) {
        try {
          infoWindow.close();
        } catch (e) {}
      }
    });
    setMarkers([]);
    // Add new markers if map exists and in map view
    if (map && viewMode === 'map') {
      addMarkersToMap(map);
    }
    // eslint-disable-next-line
  }, [events, map, viewMode]);

  // Clean up map instance and markers on unmount
  useEffect(() => {
    return () => {
      markers.forEach(({ marker, infoWindow }) => {
        if (marker) {
          try {
            marker.setMap(null);
          } catch (e) {}
        }
        if (infoWindow && infoWindow.close) {
          try {
            infoWindow.close();
          } catch (e) {}
        }
      });
      setMarkers([]);
      clearMapChildren();
    };
    // eslint-disable-next-line
  }, []);

  const addMarkersToMap = (mapInstance) => {
    if (!mapInstance || !window.google) return;

    // Remove old markers
    markers.forEach(({ marker }) => {
      try {
        marker.setMap(null);
      } catch (e) {}
    });

    const newMarkers = events.map((event) => {
      const marker = new window.google.maps.Marker({
        position: event.coordinates,
        map: mapInstance,
        title: event.title,
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: new window.google.maps.Size(30, 30)
        }
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: createInfoWindowContent(event)
      });

      marker.addListener('click', () => {
        setSelectedEvent(event);
        infoWindow.open(mapInstance, marker);
      });

      return { marker, infoWindow };
    });

    setMarkers(newMarkers);
  };

  const createInfoWindowContent = (event) => {
    return `
      <div style="padding: 10px; max-width: 300px;">
        <h3 style="margin: 0 0 10px 0; color: #236bbf;">${event.title}</h3>
        <p style="margin: 5px 0; color: #666;">${event.description}</p>
        <p style="margin: 5px 0;"><strong>Date:</strong> ${formatDate(event.date, event.time)}</p>
        <p style="margin: 5px 0;"><strong>Location:</strong> ${event.location}</p>
        <p style="margin: 5px 0;"><strong>Organization:</strong> ${event.organization}</p>
        <p style="margin: 5px 0;"><strong>Volunteers:</strong> ${event.currentVolunteers}/${event.maxVolunteers}</p>
      </div>
    `;
  };

  const handleAddEvent = () => {
    if (!newEvent.title || !newEvent.description || !newEvent.date || !newEvent.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    const event = {
      ...newEvent,
      id: Date.now(),
      currentVolunteers: 0,
      coordinates: { lat: parseFloat(newEvent.coordinates.lat), lng: parseFloat(newEvent.coordinates.lng) }
    };

    setEvents([...events, event]);
    setNewEvent({
      title: '',
      description: '',
      date: '',
      time: '',
      duration: '',
      location: '',
      address: '',
      coordinates: { lat: 40.7589, lng: -73.9851 },
      organization: '',
      maxVolunteers: '',
      category: '',
      status: 'active'
    });
    setShowAddDialog(false);
    toast.success('Event added successfully!');
    
    // Refresh map markers if in map view
    if (map && viewMode === 'map') {
      addMarkersToMap(map);
    }
  };

  const handleEditEvent = () => {
    if (!editingEvent.title || !editingEvent.description || !editingEvent.date || !editingEvent.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    const updatedEvents = events.map(event =>
      event.id === editingEvent.id ? { ...editingEvent } : event
    );
    setEvents(updatedEvents);
    setShowEditDialog(false);
    setEditingEvent(null);
    toast.success('Event updated successfully!');
    
    // Refresh map markers if in map view
    if (map && viewMode === 'map') {
      addMarkersToMap(map);
    }
  };

  const handleDeleteEvent = (eventId) => {
    setEvents(events.filter(event => event.id !== eventId));
    toast.success('Event deleted successfully!');
    
    // Refresh map markers if in map view
    if (map && viewMode === 'map') {
      addMarkersToMap(map);
    }
  };

  const handleMarkerClick = (event) => {
    setSelectedEvent(event);
  };

  const handleMapClick = () => {
    setSelectedEvent(null);
  };

  const openEditDialog = (event) => {
    setEditingEvent({ ...event });
    setShowEditDialog(true);
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={status === 'active' ? 'Active' : 'Inactive'}
        color={status === 'active' ? 'success' : 'default'}
        size="small"
      />
    );
  };

  const formatDate = (date, time) => {
    try {
      const dateTime = new Date(`${date}T${time}`);
      return format(dateTime, 'MMM dd, yyyy h:mm a');
    } catch (error) {
      return `${date} at ${time}`;
    }
  };

  const handleSearch = async (query) => {
    // If your API supports search:
    // const results = await volassisAPI.searchEvents(query);
    // setFilteredEvents(results);

    // Otherwise, filter locally:
    setFilteredEvents(events.filter(e =>
      e.title.toLowerCase().includes(query.toLowerCase()) ||
      (e.location && e.location.toLowerCase().includes(query.toLowerCase()))
    ));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Volunteer Events
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant={viewMode === 'map' ? 'contained' : 'outlined'}
            startIcon={<MapIcon />}
            onClick={() => setViewMode('map')}
          >
            Map View
          </Button>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            startIcon={<ListIcon />}
            onClick={() => setViewMode('list')}
          >
            List View
          </Button>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddDialog(true)}
            >
              Add Event
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SearchBar onSearch={handleSearch} />

      {viewMode === 'map' ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Events Map
            </Typography>
            <div 
              ref={mapRef} 
              style={mapContainerStyle}
              onClick={handleMapClick}
            >
              {mapError ? (
                <Box 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  height="100%"
                  bgcolor="grey.100"
                >
                  <Typography color="error">
                    Map could not be loaded. Please check your Google Maps API key.
                  </Typography>
                </Box>
              ) : !map ? (
                <Box 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  height="100%"
                  bgcolor="grey.100"
                >
                  <Typography>Loading map...</Typography>
                </Box>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredEvents.map((event) => (
            <Grid item xs={12} md={6} lg={4} key={event.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="div">
                      {event.title}
                    </Typography>
                    {getStatusChip(event.status)}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {event.description}
                  </Typography>
                  
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <EventIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {formatDate(event.date, event.time)}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {event.location}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" gutterBottom>
                    <strong>Organization:</strong> {event.organization}
                  </Typography>
                  
                  <Typography variant="body2" gutterBottom>
                    <strong>Duration:</strong> {event.duration} hours
                  </Typography>
                  
                  <Typography variant="body2" gutterBottom>
                    <strong>Volunteers:</strong> {event.currentVolunteers}/{event.maxVolunteers}
                  </Typography>
                  
                  <Chip label={event.category} size="small" sx={{ mb: 2 }} />
                  
                  {isAdmin && (
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => openEditDialog(event)}
                        fullWidth
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteEvent(event.id)}
                        fullWidth
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Event Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Event</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event Title"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newEvent.category}
                  label="Category"
                  onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={newEvent.time}
                onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (hours)"
                type="number"
                value={newEvent.duration}
                onChange={(e) => setNewEvent({ ...newEvent, duration: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Volunteers"
                type="number"
                value={newEvent.maxVolunteers}
                onChange={(e) => setNewEvent({ ...newEvent, maxVolunteers: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location Name"
                value={newEvent.location}
                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Organization"
                value={newEvent.organization}
                onChange={(e) => setNewEvent({ ...newEvent, organization: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={newEvent.address}
                onChange={(e) => setNewEvent({ ...newEvent, address: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={newEvent.coordinates.lat}
                onChange={(e) => setNewEvent({
                  ...newEvent,
                  coordinates: { ...newEvent.coordinates, lat: e.target.value }
                })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={newEvent.coordinates.lng}
                onChange={(e) => setNewEvent({
                  ...newEvent,
                  coordinates: { ...newEvent.coordinates, lng: e.target.value }
                })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddEvent} variant="contained">Add Event</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          {editingEvent && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Event Title"
                  value={editingEvent.title}
                  onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={editingEvent.category}
                    label="Category"
                    onChange={(e) => setEditingEvent({ ...editingEvent, category: e.target.value })}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={editingEvent.description}
                  onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={editingEvent.date}
                  onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Time"
                  type="time"
                  value={editingEvent.time}
                  onChange={(e) => setEditingEvent({ ...editingEvent, time: e.target.value })}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (hours)"
                  type="number"
                  value={editingEvent.duration}
                  onChange={(e) => setEditingEvent({ ...editingEvent, duration: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Volunteers"
                  type="number"
                  value={editingEvent.maxVolunteers}
                  onChange={(e) => setEditingEvent({ ...editingEvent, maxVolunteers: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location Name"
                  value={editingEvent.location}
                  onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
                  required
                />
              </Grid>
              {/* FIXED: Edit Event Dialog - Organization field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Organization"
                  value={editingEvent.organization}
                  onChange={(e) => setEditingEvent({ ...editingEvent, organization: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={editingEvent.address}
                  onChange={(e) => setEditingEvent({ ...editingEvent, address: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={editingEvent.coordinates.lat}
                  onChange={(e) => setEditingEvent({
                    ...editingEvent,
                    coordinates: { ...editingEvent.coordinates, lat: e.target.value }
                  })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={editingEvent.coordinates.lng}
                  onChange={(e) => setEditingEvent({
                    ...editingEvent,
                    coordinates: { ...editingEvent.coordinates, lng: e.target.value }
                  })}
                  required
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditEvent} variant="contained">Update Event</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }
    return this.props.children;
  }
}

// Usage Example
// <ErrorBoundary>
//   <Events />
// </ErrorBoundary>

export default Events;
